import React, { useState, useRef, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import axios from "axios";
import _ from "lodash";
import {
  Autocomplete as GoogleAutocomplete,
} from "@react-google-maps/api";
import dayjs from "dayjs";
import Cookies from "js-cookie";

import {
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Typography,
  Container,
  FormHelperText,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

import {
  RegularizeMeetingValidationSchema,
  TagMeetingValidationSchema,
} from "../utils/validation";

import constant from "../utils/constants";
import serviceEndpoints from "../services/serviceEndpoints";

import { convertDateToRegulariseDate, getCurrentTime } from "../utils/utils";
import { useAuth } from "../context/AuthProvider";
import { axiosInstance } from "../service/axiosInstance";

const TagRegularizeMeetingForm = ({ meetingName, setShowSuccess, date }) => {
  const [startPointAddress, setStartPointAddress] = useState(null);
  const [startPointLatLng, setStartPointLatLng] = useState(null);
  const [meetingAddress, setMeetingAddress] = useState(null);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const autocompleteRef2 = useRef(null);
  const inputRef2 = useRef(null);
  const [tagMeetingCoordinates, setTagMeetingCoordinates] = useState({
    lat: null,
    lng: null,
  });
  const [areaOfPartnerAddress, setAreaOfPartnerAddress] = useState();
  const [regulariseMeetingCoordinates, setRegulariseMeetingCoordinates] =
    useState({
      lat: null,
      lng: null,
    });
  const [imdCodeList, setImdCodeList] = useState([]);
  const [imdNameList, setImdNameList] = useState([]);
  const [imdDetails, setImdDetails] = useState([]);
  const [errorFetchingCurrentLocation, setErrorFetchingCurrentLocation] =
    useState(false);
  const { userDetail } = useAuth();

  const fetchLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.log("Geolocation is not supported by your browser.");
      setErrorFetchingCurrentLocation(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        getAddressFromLatLng(latitude, longitude);
        setTagMeetingCoordinates({ lat: latitude, lng: longitude });
      },
      (err) => {
        console.log(`Error fetching location: ${err.message}`);
        setErrorFetchingCurrentLocation(true);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, []);

  // Function to reverse geocode lat/lng to address
  const getAddressFromLatLng = async (lat, lng) => {
    const cacheKey = `${lat},${lng}`;
    const cachedAddress = Cookies.get(cacheKey);
    if (cachedAddress) {
      setMeetingAddress(cachedAddress);
      return;
    }

    try {
      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json`,
        {
          params: {
            latlng: `${lat},${lng}`,
            key: `${process.env.REACT_APP_GOOGLE_API_KEY}`,
            result_type: "street_address",
          },
        }
      );

      if (response.data.status === "OK") {
        console.log("response", response);
        const formattedAddress = response.data.results[0].formatted_address;
        Cookies.set(cacheKey, formattedAddress, {
          expires: constant.cache_expiry_minutes / (24 * 60),
        });
        setMeetingAddress(formattedAddress);
      } else {
        setErrorFetchingCurrentLocation(true);
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      setErrorFetchingCurrentLocation(true);
    }
  };

  // Handle place selection
  const handlePlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.geometry) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const postalCodeComponent = place.address_components.find((component) =>
        component.types.includes("postal_code")
      );
      const pincode = postalCodeComponent && postalCodeComponent?.long_name;

      if (pincode) {
        setFieldValue("pincode", pincode);
      }
      setFieldValue("startPoint", place.formatted_address);
      setStartPointAddress(place.formatted_address);
      setStartPointLatLng({ lat, lng });
    }
  };

  //Area of partner change for regularize meeting type
  const handleAreaOfPartnerPlaceChange = () => {
    const place = autocompleteRef2.current.getPlace();
    if (place && place.geometry) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      // Update state with place information and coordinates
      setFieldValue("endPoint", place.formatted_address);

      setAreaOfPartnerAddress(place.formatted_address);
      setRegulariseMeetingCoordinates({ lat, lng });
    }
  };

  //Area of partner change for tag meeting if current location is not fetched
  const handleTagMeetingAreaOfPartnerChange = () => {
    const place = autocompleteRef2.current.getPlace();
    if (place && place.geometry) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      // Update state with place information and coordinates
      setFieldValue("endPoint", place.formatted_address);
      setMeetingAddress(place.formatted_address);
      setTagMeetingCoordinates({ lat, lng });
    }
  };

  const { handleSubmit, handleChange, touched, errors, values, setFieldValue } =
    useFormik({
      initialValues: {
        startPoint: "",
        type: "",
        imdCode: "",
        imdName: "",
        meetingWith: "",
        purpose: "",
        remarks: "",
        status: "",
        // hours: "",
        mins: "",
        pincode: "",
        endPoint: "",
      },

      onSubmit: (values) => tagMeeting(values),
      validationSchema:
        meetingName === constant.regularize_type
          ? RegularizeMeetingValidationSchema
          : TagMeetingValidationSchema,
    });

  // Handle form submission
  const tagMeeting = async (values) => {
    let payload = {
      startLocationAddress: startPointAddress,
      startLocationCoordinates: startPointLatLng,
      meetingAddress:
        meetingName === constant.regularize_type
          ? areaOfPartnerAddress
          : meetingAddress,
      meetingCoordinates:
        meetingName === constant.regularize_type
          ? regulariseMeetingCoordinates
          : tagMeetingCoordinates,
      type: values.type,
      imdCode: values.imdCode,
      imdName: values.imdName,
      meetingWith: values.meetingWith,
      purpose: values.purpose,
      remarks: values.remarks,
      status: values.status,
      duration: values.mins,
      pincode: values.pincode,
      isRegularise: meetingName === constant.regularize_type ? true : false,
    };
    if (meetingName === "regularize") {
      payload = {
        ...payload,
        date: date,
        time: getCurrentTime(),
      };
    } else {
      payload = {
        ...payload,
        date: dayjs().format(constant.backendDateFormat),
        time: getCurrentTime(),
      };
    }
    try {
      await axiosInstance.post(serviceEndpoints.tagRegularizeMeetingEndpoint, {
        ...payload,
      });
      setShowSuccess(true);
    } catch (error) {
      console.log(error);
    }
  };

  // const hoursOptions = Array.from({ length: 24 }, (_, i) => i);
  // const minutesOptions = Array.from({ length: 60 }, (_, i) => i);

  const minutesOptions = [15, 30, 45, 60];

  useEffect(() => {
    if (meetingName !== constant.regularize_type) {
      fetchLocation();
    }
  }, [meetingName, fetchLocation]);

  // Logic for Handling IMD details

  const fetchIMDData = async (key, value) => {
    try {
      const payload = key === "imdCode" ? { code: value } : { name: value };
      const res = await axiosInstance.post(
        serviceEndpoints.searchImdsEndpoint,
        {
          ...payload,
        }
      );

      const imds = res.data.imds || [];
      const imdCodes = imds.map((imd) => imd.code);
      const imdNames = imds.map((imd) => imd.name);

      setImdCodeList(imdCodes);
      setImdNameList(imdNames);
      setImdDetails(imds);
    } catch (error) {
      console.error("Error fetching IMD data", error);
    }
  };

  const debouncedFetchIMDData = _.debounce(fetchIMDData, 500);

  const handleInputChange = (key) => (event, newValue) => {
    setFieldValue((prev) => ({ ...prev, [key]: newValue }));

    if (newValue?.length > 2) {
      debouncedFetchIMDData(key, newValue);
    } else {
      setImdNameList([]);
      setImdCodeList([]);
    }
  };

  const debouncedHandleImdDetails = _.debounce(
    (key, value, imdDetails, setFieldValue) => {
      if (key === "imdCode") {
        const imd = imdDetails.find((imd) => imd.code === value);
        setFieldValue("imdName", imd?.name || "");
      } else {
        const imd = imdDetails.find((imd) => imd.name === value);
        setFieldValue("imdCode", imd?.code || "");
      }
    },
    300
  );

  const handleImdDetails = (key, value) => {
    debouncedHandleImdDetails(key, value, imdDetails, setFieldValue);
  };

  return (
    <Container
      maxWidth="sm"
      sx={{ marginTop: 4, marginBottom: 2, paddingBottom: "80px" }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          console.log("Errors:", errors);
          handleSubmit();
        }}
      >
        {meetingName === constant.regularize_type && (
          <Typography variant="h6" className="typography_heading date_message">
            Regularizing for {date ? convertDateToRegulariseDate(date) : "NA"}
          </Typography>
        )}
        <Box
          sx={{
            alignItems: "center",
            borderRadius: "30px",
            padding: 2,
            // border: "1px solid #ddd",
            flexDirection: "row",
          }}
          className="container_bg"
        >
          <Typography variant="h6" className="typography_heading form_head">
            Enter details
          </Typography>
          {/* Start Point */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography
              variant="h6"
              className="typography_heading form_sub_head"
            >
              Start point <span className="mandatory_mark"> *</span>
            </Typography>
              <GoogleAutocomplete
                onLoad={(ref) => (autocompleteRef.current = ref)}
                onPlaceChanged={handlePlaceChanged}
              >
                <TextField
                  // required={true}
                  ref={inputRef}
                  fullWidth
                  error={touched.startPoint && Boolean(errors.startPoint)}
                  helperText={touched.startPoint && errors.startPoint}
                  variant="outlined"
                  label="Enter a location"
                />
              </GoogleAutocomplete>
          </Box>
          {/* Meeting Type */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "15px",
                marginBottom: "8px",
              }}
              className="typography_heading form_sub_head"
            >
              Meeting type <span className="mandatory_mark"> *</span>
            </Typography>
            <FormControl fullWidth error={touched.type && Boolean(errors.type)}>
              <InputLabel id="demo-simple-select-label">
                Meeting type
              </InputLabel>
              <Select
                name="type"
                label="Meeting type"
                labelId="meeting-type-label"
                value={values.type}
                onChange={handleChange}
                error={touched.type && Boolean(errors.type)}
                helperText={touched.type && errors.type}
              >
                {Object.entries(userDetail?.meta?.MEETING_TYPES || {}).map(
                  ([key, value], index) => (
                    <MenuItem key={key} value={value}>
                      {value}
                    </MenuItem>
                  )
                )}
              </Select>
              <FormHelperText>{touched.type && errors.type}</FormHelperText>
            </FormControl>
          </Box>
          {/* IMD Name */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "15px",
                marginBottom: "8px",
              }}
              className="typography_heading form_sub_head"
            >
              IMD name <span className="mandatory_mark"> *</span>
            </Typography>
            {values.type === constant.meetingTypeNew ? (
              <TextField
                fullWidth
                onChange={handleChange}
                variant="outlined"
                name="imdName"
                inputProps={{ maxLength: 100 }}
                value={values.imdName}
                label="Enter IMD name"
                error={touched.imdName && Boolean(errors.imdName)}
                helperText={touched.imdName && errors.imdName}
              />
            ) : (
              <Autocomplete
                options={imdNameList}
                onChange={(event, newValue) => {
                  setFieldValue("imdName", newValue);
                  handleImdDetails("imdName", newValue);
                }}
                name="imdName"
                value={values.imdName}
                onInputChange={handleInputChange("imdName")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Enter IMD name"
                    // required={true}
                    error={touched.imdName && Boolean(errors.imdName)}
                    helperText={touched.imdName && errors.imdName}
                  />
                )}
              />
            )}
          </Box>
          {/* IMD Code */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "15px",
                marginBottom: "8px",
              }}
              className="typography_heading form_sub_head"
            >
              IMD code{" "}
              {values.type !== constant.meetingTypeNew && (
                <span className="mandatory_mark"> *</span>
              )}
            </Typography>
            {values.type === constant.meetingTypeNew ? (
              <TextField
                fullWidth
                variant="outlined"
                type="number"
                onChange={handleChange}
                name="imdCode"
                inputProps={{ maxLength: 100 }}
                value={values.imdCode}
                label="Enter IMD code"
                error={touched.imdCode && Boolean(errors.imdCode)}
                helperText={touched.imdCode && errors.imdCode}
              />
            ) : (
              <Autocomplete
                options={imdCodeList || []}
                onChange={(event, newValue) => {
                  setFieldValue("imdCode", newValue);
                  handleImdDetails("imdCode", newValue);
                }}
                onInputChange={handleInputChange("imdCode")}
                name="imdCode"
                value={values.imdCode}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    // required={true}
                    label="Enter IMD code"
                    error={touched.imdCode && Boolean(errors.imdCode)}
                    helperText={touched.imdCode && errors.imdCode}
                  />
                )}
              />
            )}
          </Box>
          {/* Meeting with */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "15px",
                marginBottom: "8px",
              }}
              className="typography_heading form_sub_head"
            >
              Meeting with <span className="mandatory_mark"> *</span>
            </Typography>
            <TextField
              fullWidth
              label="Enter person/people name(s)"
              variant="outlined"
              name="meetingWith"
              // required={true}
              onChange={handleChange}
              inputProps={{ maxLength: 100 }}
              value={values.meetingWith}
              error={touched.meetingWith && Boolean(errors.meetingWith)}
              helperText={touched.meetingWith && errors.meetingWith}
            />
          </Box>
          {/* Purpose */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "15px",
                marginBottom: "8px",
              }}
              className="typography_heading form_sub_head"
            >
              Purpose <span className="mandatory_mark"> *</span>
            </Typography>
            <TextField
              fullWidth
              label="Brief about the discussion"
              variant="outlined"
              name="purpose"
              // required={true}
              onChange={handleChange}
              inputProps={{ maxLength: 250 }}
              value={values.purpose}
              error={touched.purpose && Boolean(errors.purpose)}
              helperText={touched.purpose && errors.purpose}
              multiline
              rows={2}
            />
          </Box>
          {/* Remarks */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "15px",
                marginBottom: "8px",
              }}
              className="typography_heading form_sub_head"
            >
              Remarks <span className="mandatory_mark"> *</span>
            </Typography>
            <TextField
              fullWidth
              label="Add remarks"
              // required={true}
              variant="outlined"
              onChange={handleChange}
              name="remarks"
              inputProps={{ maxLength: 250 }}
              value={values.remarks}
              error={touched.remarks && Boolean(errors.remarks)}
              helperText={touched.remarks && errors.remarks}
            />
          </Box>
          {/* Status */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "15px",
                marginBottom: "8px",
              }}
              className="typography_heading form_sub_head"
            >
              Status <span className="mandatory_mark"> *</span>
            </Typography>
            <FormControl
              fullWidth
              error={touched.status && Boolean(errors.status)}
            >
              <InputLabel id="demo-simple-select-label">Status</InputLabel>
              <Select
                name="status"
                label="Select status"
                labelId="meeting-type-label"
                value={values.status}
                onChange={handleChange}
                error={touched.status && Boolean(errors.status)}
              >
                {Object.values(userDetail?.meta?.MEETING_STATUS || {})?.map(
                  (status, index) => {
                    return (
                      <MenuItem key={index} value={status}>
                        {status}
                      </MenuItem>
                    );
                  }
                )}
              </Select>
              <FormHelperText>{touched.status && errors.status}</FormHelperText>
            </FormControl>
          </Box>
          {/* Duration */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "15px",
                marginBottom: "8px",
              }}
              className="typography_heading form_sub_head"
            >
              Duration <span className="mandatory_mark"> *</span>
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* <FormControl
                fullWidth
                error={touched.hours && Boolean(errors.hours)}
              >
                <InputLabel id="demo-simple-select-label">Hours</InputLabel>
                <Select
                  name="hours"
                  label="hours"
                  labelId="meeting-type-label"
                  value={values.hours}
                  onChange={handleChange}
                >
                  {hoursOptions.map((hour) => (
                    <MenuItem key={hour} value={hour}>
                      {hour}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{touched.hours && errors.hours}</FormHelperText>
              </FormControl> */}
              <FormControl
                fullWidth
                error={touched.mins && Boolean(errors.mins)}
              >
                <InputLabel id="demo-simple-select-label">Minutes</InputLabel>
                <Select
                  name="mins"
                  label="mins"
                  labelId="meeting-type-label"
                  value={values.mins}
                  onChange={handleChange}
                >
                  {minutesOptions.map((minute) => (
                    <MenuItem key={minute} value={minute}>
                      {`${minute} mins`}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{touched.mins && errors.mins}</FormHelperText>
              </FormControl>
            </Box>
          </Box>
          {/* Area of Partner */}
          {(meetingName === constant.regularize_type ||
            (meetingName === constant.tag_meeting &&
              errorFetchingCurrentLocation)) && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "15px",
                  marginBottom: "8px",
                }}
                className="typography_heading form_sub_head"
              >
                Area of partner <span className="mandatory_mark"> *</span>
              </Typography>
                <GoogleAutocomplete
                  onLoad={(ref) => (autocompleteRef2.current = ref)}
                  onPlaceChanged={
                    meetingName === constant.regularize_type
                      ? handleAreaOfPartnerPlaceChange
                      : handleTagMeetingAreaOfPartnerChange
                  }
                >
                  <TextField
                    // required={true}
                    ref={inputRef2}
                    fullWidth
                    error={touched.endPoint && Boolean(errors.endPoint)}
                    helperText={touched.endPoint && errors.endPoint}
                    variant="outlined"
                    label="Enter a location"
                  />
                </GoogleAutocomplete>
            </Box>
          )}
          {/* Pincode */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "15px",
                marginBottom: "8px",
              }}
              className="typography_heading form_sub_head"
            >
              Pincode
              {/* <span className="mandatory_mark"> *</span> */}
            </Typography>
            <TextField
              fullWidth
              type="number"
              variant="outlined"
              label="Enter pincode"
              name="pincode"
              // required={true}
              inputProps={{ maxLength: 6 }}
              value={values.pincode}
              onChange={(event) => setFieldValue("pincode", event.target.value)}
              error={touched.pincode && Boolean(errors.pincode)}
              helperText={touched.pincode && errors.pincode}
            />
          </Box>{" "}
        </Box>
        <Box sx={{ marginTop: 2 }}>
          <Button type="submit" variant="contained" fullWidth>
            Submit
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default TagRegularizeMeetingForm;
